/**
 * FilterPanel — Sidebar section [4]: `list` slot.
 *
 * Three multi-selects (period / movement / genre) drive the AuthorListView.
 * Emits `filter:change` on the bus with `AuthorFilters` payload whenever
 * the selection changes; the list view applies them locally (bundled data).
 *
 * Implementation note: lightweight checkbox lists collapsed into <details>
 * sections to keep the sidebar compact on smaller screens.
 */

import { BaseSection, type SectionId } from '@/core/BaseSection';
import type { Locale } from '@/bridge/bridge-types';
import type { AuthorFilters } from '@/services/IDataService';
import type { Period, Movement, Genre } from '@/data/schema';
import { PERIOD_VOCAB, MOVEMENT_VOCAB, GENRE_VOCAB } from '@/data/vocab';
import { escapeHtml } from '@/core/escape';
import type { UiLocale, VocabEntry } from '@/data/vocab';

export class FilterPanel extends BaseSection {
  readonly sectionId: SectionId = 'list';
  readonly sectionTitle = 'Filter';

  private selectedPeriods = new Set<Period>();
  private selectedMovements = new Set<Movement>();
  private selectedGenres = new Set<Genre>();

  protected async onInit(): Promise<void> {
    // Single delegated listeners attached ONCE for the component lifetime.
    this.addEventListener(this.element, 'click', (ev) => {
      const t = ev.target as HTMLElement;
      if (t.closest('[data-action="clear"]')) {
        this.clearAll();
      }
      if (t.closest('[data-action="open-overlay"]')) {
        this.openOverlay();
      }
    });
    this.addEventListener(this.element, 'change', (ev) => {
      const input = ev.target as HTMLInputElement;
      if (input.type !== 'checkbox') return;
      const group = input.dataset.group as 'period' | 'movement' | 'genre';
      const id = input.value;
      const set = this.setFor(group);
      if (input.checked) set.add(id as never);
      else set.delete(id as never);
      this.emitChange();
    });

    this.render();
  }

  protected onLocaleChange(_locale: Locale): void {
    this.closeOverlay();
    this.render();
  }

  /* ── Pannello filtri a tutto schermo (solo LIM) ──
   * Appeso a document.body: dentro la sidebar resterebbe clippato dal suo
   * transform/overflow. Listener sul pannello stesso → muore con l'elemento. */
  private overlay: HTMLElement | null = null;

  private openOverlay(): void {
    this.closeOverlay();
    // Il drawer sotto coprirebbe metà pannello: via (stesso evento del navigate).
    this.context.bus.emit('sidebar:close', {});
    const locale = this.context.locale as UiLocale;
    const el = document.createElement('div');
    el.className = 'fp-lim-overlay';
    el.innerHTML = `
      <div class="fp-lim-panel" role="dialog" aria-label="${this.t('filter.title')}">
        <header class="fp-lim-head">
          <h2 class="fp-title">${this.t('filter.title')}</h2>
          <button type="button" class="fp-clear" data-action="clear">${this.t('filter.clear')}</button>
          <button type="button" class="fp-lim-close" data-action="close" aria-label="${this.t('filter.close')}">✕</button>
        </header>
        <div class="fp-lim-cols">
          ${this.renderOverlayGroup('period', this.t('filter.period'), PERIOD_VOCAB, this.selectedPeriods, locale)}
          ${this.renderOverlayGroup('movement', this.t('filter.movement'), MOVEMENT_VOCAB, this.selectedMovements, locale)}
          ${this.renderOverlayGroup('genre', this.t('filter.genre'), GENRE_VOCAB, this.selectedGenres, locale)}
        </div>
      </div>
    `;
    el.addEventListener('click', (ev) => {
      const t = ev.target as HTMLElement;
      if (t === el || t.closest('[data-action="close"]')) {
        this.closeOverlay();
        return;
      }
      if (t.closest('[data-action="clear"]')) {
        this.selectedPeriods.clear();
        this.selectedMovements.clear();
        this.selectedGenres.clear();
        el.querySelectorAll<HTMLButtonElement>('.fp-chip[aria-pressed="true"]')
          .forEach(c => c.setAttribute('aria-pressed', 'false'));
        this.emitChange();
        this.render();
        return;
      }
      const chip = t.closest<HTMLButtonElement>('.fp-chip');
      if (chip) {
        const group = chip.dataset.group as 'period' | 'movement' | 'genre';
        const set = this.setFor(group);
        const id = chip.dataset.id!;
        const on = chip.getAttribute('aria-pressed') !== 'true';
        if (on) set.add(id as never);
        else set.delete(id as never);
        chip.setAttribute('aria-pressed', String(on));
        this.emitChange();
        this.render(); // aggiorna il contatore sul bottone del drawer
      }
    });
    document.body.appendChild(el);
    this.overlay = el;
  }

  private closeOverlay(): void {
    this.overlay?.remove();
    this.overlay = null;
  }

  private renderOverlayGroup<T extends string>(
    group: 'period' | 'movement' | 'genre',
    title: string,
    vocab: ReadonlyArray<VocabEntry<T>>,
    selected: Set<T>,
    locale: UiLocale,
  ): string {
    const ordered = [...vocab].sort((a, b) => a.order - b.order);
    return `
      <section class="fp-lim-group" data-group="${group}">
        <h3 class="fp-lim-group-title">${title}</h3>
        <div class="fp-lim-chips">
          ${ordered.map(entry => {
            const label = locale === 'en' ? entry.label_en : entry.label_it;
            return `
              <button type="button" class="fp-chip" data-group="${group}"
                data-id="${entry.id}" aria-pressed="${selected.has(entry.id)}">
                ${escapeHtml(label)}
              </button>
            `;
          }).join('')}
        </div>
      </section>
    `;
  }

  private render(): void {
    const locale = this.context.locale as UiLocale;
    this.element.classList.add('filter-panel');
    if (this.isLim()) {
      // Su LIM i 3 accordion (47 voci) non stanno nel drawer: un solo bottone
      // apre un pannello a tutto schermo con le categorie in colonne di chip.
      const total =
        this.selectedPeriods.size + this.selectedMovements.size + this.selectedGenres.size;
      this.element.innerHTML = `
        <header class="fp-header">
          <h2 class="fp-title">${this.t('filter.title')}</h2>
          <button type="button" class="fp-clear" data-action="clear">${this.t('filter.clear')}</button>
        </header>
        <button type="button" class="fp-lim-open" data-action="open-overlay">
          <span class="fp-lim-open-label">${this.t('filter.choose')}</span>
          ${total > 0 ? `<span class="fp-group-count">${total}</span>` : ''}
        </button>
      `;
      return;
    }
    this.element.innerHTML = `
      <header class="fp-header">
        <h2 class="fp-title">${this.t('filter.title')}</h2>
        <button type="button" class="fp-clear" data-action="clear">${this.t('filter.clear')}</button>
      </header>
      <div class="fp-scroll">
        ${this.renderGroup('period', this.t('filter.period'), PERIOD_VOCAB, this.selectedPeriods, locale)}
        ${this.renderGroup('movement', this.t('filter.movement'), MOVEMENT_VOCAB, this.selectedMovements, locale)}
        ${this.renderGroup('genre', this.t('filter.genre'), GENRE_VOCAB, this.selectedGenres, locale)}
      </div>
    `;
  }

  private isLim(): boolean {
    return document.documentElement.dataset.surface === 'lim';
  }

  private renderGroup<T extends string>(
    group: 'period' | 'movement' | 'genre',
    title: string,
    vocab: ReadonlyArray<VocabEntry<T>>,
    selected: Set<T>,
    locale: UiLocale,
  ): string {
    const ordered = [...vocab].sort((a, b) => a.order - b.order);
    const count = selected.size;
    // Su TELEFONO il drawer è largo (~80%) e c'è spazio: tutti i gruppi
    // partono APERTI e l'accordion NON è esclusivo (senza name= più gruppi
    // restano aperti insieme). Su PC/tablet: chiusi di default (aperti se
    // c'è una selezione), accordion esclusivo per non allungare la sidebar.
    const mobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const openAttr = count > 0 || mobile ? 'open' : '';
    return `
      <details class="fp-group" ${mobile ? '' : 'name="fp-filters"'} data-group="${group}" ${openAttr}>
        <summary class="fp-group-title">
          <span class="fp-group-label">${title}</span>
          ${count > 0 ? `<span class="fp-group-count">${count}</span>` : ''}
        </summary>
        <ul class="fp-options" role="list">
          ${ordered.map(entry => {
            const label = locale === 'en' ? entry.label_en : entry.label_it;
            const checked = selected.has(entry.id) ? 'checked' : '';
            return `
              <li class="fp-option">
                <label>
                  <input type="checkbox" data-group="${group}" value="${entry.id}" ${checked}/>
                  <span>${escapeHtml(label)}</span>
                </label>
              </li>
            `;
          }).join('')}
        </ul>
      </details>
    `;
  }

  private setFor(group: 'period' | 'movement' | 'genre'): Set<string> {
    if (group === 'period') return this.selectedPeriods as Set<string>;
    if (group === 'movement') return this.selectedMovements as Set<string>;
    return this.selectedGenres as Set<string>;
  }

  private clearAll(): void {
    this.selectedPeriods.clear();
    this.selectedMovements.clear();
    this.selectedGenres.clear();
    this.render();
    this.emitChange();
  }

  private emitChange(): void {
    const filters: AuthorFilters = {};
    if (this.selectedPeriods.size > 0) filters.periods = [...this.selectedPeriods];
    if (this.selectedMovements.size > 0) filters.movements = [...this.selectedMovements];
    if (this.selectedGenres.size > 0) filters.genres = [...this.selectedGenres];
    this.context.bus.emit<AuthorFilters>('filter:change', filters);
  }

  private t(key: string): string {
    return this.context.i18n.t(key);
  }
}

